/**
 * Per-report audit log (#29) — the V2-style "When · Actor · Event · Detail"
 * table, scoped to one report and human-readable. Lives at the bottom of the
 * Report view (replaces the useless global "Activity log" of identical
 * run/OK/sonnet rows). Reads audit_log via /activity?report_id.
 */
import { useEffect, useState } from "react";
import { Alert, Box, ExpandableSection, Spinner, Table } from "@cloudscape-design/components";
import { api } from "@/core/api/client";
import { formatET } from "@/core/ui/time";

interface AuditRow {
  id: string;
  event_type: string;
  actor_user_id: string | null;
  actor_email: string | null;
  metadata: {
    aip?: string;
    model_id?: string;
    duration_ms?: number;
    result?: string;
    outcome?: string;
    error_class?: string;
    error_message?: string;
    detail?: string;
  };
  created_at: string;
}

/** #101: a display row — consecutive same-AIP runs (AIP-4/AIP-6 fire per
 *  exception/CUEC) collapse into one row with a count + total duration, so the
 *  log reads as a digest instead of N identical lines. */
interface DisplayRow {
  row: AuditRow;
  count: number;
  totalMs: number;
}

const AIP_LABELS: Record<string, string> = {
  aip_1_scope: "AIP-1 Scope",
  aip_2_carveout: "AIP-2 Carve-out",
  aip_3_controls: "AIP-3 Controls",
  aip_4_exception: "AIP-4 Exception",
  aip_5_cuec: "AIP-5 CUEC extract",
  aip_6_mapper: "AIP-6 CUEC mapping",
  aip_7_bridge: "AIP-7 Bridge letter",
};

function actorOf(r: AuditRow): string {
  // #110: show WHO acted (the user's email), not just "You".
  if (r.actor_email) return r.actor_email;
  if (r.actor_user_id) return "You";
  if (r.metadata.aip) return "AI";
  return "System";
}

function eventOf(r: AuditRow): string {
  if (r.metadata.aip) return AIP_LABELS[r.metadata.aip] ?? r.metadata.aip;
  return (
    {
      upload: "Upload",
      scope_decision: "Scope decision",
      carveout_decision: "Carve-out decision",
      cuec_decision: "CUEC updated",
      exception_decision: "Exception updated",
      bridge_decision: "Bridge letter updated",
      "ingest_error": "Ingest error",
    }[r.event_type] ?? r.event_type
  );
}

function detailOf(d: DisplayRow): string {
  const r = d.row;
  if (r.metadata.error_message) return `${r.metadata.error_class ?? "error"}: ${r.metadata.error_message}`;
  if (r.metadata.detail) return r.metadata.detail;
  if (r.metadata.aip) {
    // Grouped AIP runs: count + total time (per-item outcomes aren't useful in a digest).
    if (d.count > 1) return `${d.count} runs · ${(d.totalMs / 1000).toFixed(1)}s total`;
    // Single run: lead with WHAT it produced (outcome), then time.
    const ms = typeof r.metadata.duration_ms === "number" ? `${r.metadata.duration_ms} ms` : "";
    const out = r.metadata.outcome?.trim();
    return [out, ms].filter(Boolean).join(" · ") || (r.metadata.result ?? "—");
  }
  return "—";
}

/** Collapse consecutive same-AIP, same-result AI rows into one display row. */
function groupRows(rows: AuditRow[]): DisplayRow[] {
  const out: DisplayRow[] = [];
  for (const r of rows) {
    const ms = typeof r.metadata.duration_ms === "number" ? r.metadata.duration_ms : 0;
    const prev = out[out.length - 1];
    const sameAip =
      !!r.metadata.aip &&
      !!prev?.row.metadata.aip &&
      eventOf(prev.row) === eventOf(r) &&
      (prev.row.metadata.result ?? "") === (r.metadata.result ?? "") &&
      !r.metadata.error_message;
    if (sameAip && prev) {
      prev.count += 1;
      prev.totalMs += ms;
    } else {
      out.push({ row: r, count: 1, totalMs: ms });
    }
  }
  return out;
}

export function ReportAuditLog({ reportId }: { reportId: string }) {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<{ events: AuditRow[] }>(`/activity?report_id=${reportId}`)
      .then((r) => !cancelled && setRows(r.events ?? []))
      // #110: surface the failure instead of silently showing "no activity" —
      // an empty log and a failed fetch must look different.
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const display = groupRows(rows);
  return (
    <ExpandableSection variant="container" headerText={`Audit log (${display.length})`}>
      {loading ? (
        <Box textAlign="center" padding="m">
          <Spinner /> Loading…
        </Box>
      ) : error ? (
        <Alert type="error" header="Couldn't load the audit log">
          {error}
        </Alert>
      ) : (
        <Table
          variant="embedded"
          items={display}
          empty={<Box color="text-body-secondary">No activity yet for this report.</Box>}
          columnDefinitions={[
            {
              id: "when",
              header: "When (ET)",
              cell: (d) => <Box fontSize="body-s" color="text-body-secondary">{formatET(new Date(d.row.created_at))}</Box>,
              width: 190,
            },
            {
              id: "actor",
              header: "Actor",
              cell: (d) => <Box fontSize="body-s" color="text-body-secondary">{actorOf(d.row)}</Box>,
              width: 90,
            },
            {
              id: "event",
              header: "Event",
              cell: (d) => (
                <Box fontSize="body-s" color="text-body-secondary">
                  {d.count > 1 ? `${eventOf(d.row)} ×${d.count}` : eventOf(d.row)}
                </Box>
              ),
              width: 200,
            },
            {
              id: "detail",
              header: "Detail",
              cell: (d) => <Box fontSize="body-s" color="text-body-secondary">{detailOf(d)}</Box>,
            },
          ]}
        />
      )}
    </ExpandableSection>
  );
}
